import { TuiElasticContainer } from '@taiga-ui/layout';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { TuiCell, TuiHint, TuiIcon, TuiTitle } from '@taiga-ui/core';
import { TuiSkeleton } from '@taiga-ui/kit';
import { SyncActionChildKey } from '../models/sync-action-child-key';
import { SyncActionDisplay } from '../models/sync-action-display';
import { SyncActionDisplayChild } from '../models/sync-action-display-child';
import { SyncCenterChildComponent } from '../sync-center-child/sync-center-child.component';
import { SyncGlobalStatusIconComponent } from '../sync-global-status-icon/sync-global-status-icon.component';

@Component({
  selector: 'pxc-sync-center-item',
  templateUrl: './sync-center-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    TuiIcon,
    SyncGlobalStatusIconComponent,
    SyncCenterChildComponent,
    TuiCell,
    TuiElasticContainer,
    TuiHint,
    TuiSkeleton,
    TuiTitle,
  ],
  host: {
    class: 'rounded-xl transition-[margin] duration-300',
    '[class.bg-background-primary-hover]': 'isExpanded()',
    '[class.m-2]': 'isExpanded()',
  },
})
export class SyncCenterItemComponent {
  readonly item = input.required<SyncActionDisplay>();
  readonly resumeChild = output<SyncActionChildKey>();
  readonly retryChild = output<SyncActionChildKey>();
  readonly deleteChild = output<SyncActionChildKey>();

  protected readonly isExpanded = signal(false);
  // The title (patient name) is empty until it is fetched for an interrupted upload restored after
  // a reload — show a skeleton for the title and subtitle until it resolves.
  protected readonly isTitleUnresolved = computed(() => !this.item().title);

  protected readonly toggle = (): void => {
    this.isExpanded.update((expanded) => !expanded);
  };

  protected readonly onResumeChild = (child: SyncActionDisplayChild): void => {
    this.resumeChild.emit(this.toChildKey(child));
  };

  protected readonly onRetryChild = (child: SyncActionDisplayChild): void => {
    this.retryChild.emit(this.toChildKey(child));
  };

  protected readonly onDeleteChild = (child: SyncActionDisplayChild): void => {
    this.deleteChild.emit(this.toChildKey(child));
  };

  private readonly toChildKey = (child: SyncActionDisplayChild): SyncActionChildKey => ({
    parentKey: this.item().key,
    childId: child.childId,
    type: child.type,
  });
}
